import { useAuthContext } from "providers/Auth";
import { useMemo } from "react";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  ApplicantStatus,
  ApplicationFeeStatus,
  ApplicationStatus,
  RequiresOverridesIdentityVerification
} from "shared/types";
import { Applicant } from "types/application";

import {
  Archive,
  ArrowRight,
  Clock,
  FlipBackward,
  Plus,
  Settings04,
  ThumbsUp
} from "untitledui-js-base";
import { canDecisionBeMade } from "utils/canMakeDecision";
import { MenuRow } from "views/applications-id/components/LayoutHeader/components/MenuRow";

export const usePopoverActions = ({
  applicants,
  onMoveClick,
  setShowActivityLog,
  viewTemplate,
  onRefundClick,
  onCancelClick,
  onUncancelClick,
  applicationStatus,
  setShowDecisionFlowModal,
  setDecisionConfirmationModal,
  setShowInviteApplicantsModal,
  requiresOverridesIdentityVerification,
  requirements
}: {
  applicationStatus: ApplicationStatus;
  viewTemplate: () => void;
  setShowActivityLog: (value: boolean) => void;
  onMoveClick?: () => void;
  onCancelClick?: () => void;
  onUncancelClick?: () => void;
  onRefundClick?: () => void;
  setShowDecisionFlowModal?: (isOpen: boolean) => void;
  setShowInviteApplicantsModal?: (isOpen: boolean) => void;
  setDecisionConfirmationModal?: (isOpen: boolean) => void;
  requiresOverridesIdentityVerification?: RequiresOverridesIdentityVerification;
  requirements?: string[];

  applicants?: Applicant[];
}) => {
  const { permissions, type } = useAuthContext();
  const { unit } = useUnitsPropertiesDictionary();

  const {
    isInviteApplicantDisabled,
    isMakeDecisionDisabled,
    isCancelApplicationDisabled,
    isRefundApplicationDisabled,
    isMoveToAnotherUnitDisabled,
    isUncancelApplicationEnabled
  } = useMemo(() => {
    const isMoveToAnotherUnitDisabled =
      (!permissions.canChangeUnit && type.isPropertyManager) ||
      [
        ApplicationStatus.Canceled,
        ApplicationStatus.Approved,
        ApplicationStatus.Rejected,
        ApplicationStatus.ConditionalApproved
      ].includes(applicationStatus);

    const isInviteApplicantDisabled = ![
      ApplicationStatus.Pending,
      ApplicationStatus.Submitted,
      ApplicationStatus.Started
    ].includes(applicationStatus);

    const isMakeDecisionDisabled = !canDecisionBeMade({
      applicants: applicants || [],
      status: applicationStatus,
      type,
      permissions,
      requirements
    });

    const isCancelApplicationDisabled = [
      ApplicationStatus.Canceled,
      ApplicationStatus.Approved,
      ApplicationStatus.Rejected,
      ApplicationStatus.ConditionalApproved
    ].includes(applicationStatus);

    const isUncancelApplicationEnabled = [ApplicationStatus.Canceled].includes(
      applicationStatus
    );

    const isRefundApplicationDisabled = !(applicants || []).some(
      (applicant: Applicant) =>
        applicant.application_fee_status === ApplicationFeeStatus.Paid &&
        applicant.application_status === ApplicantStatus.Submitted
    );

    return {
      isInviteApplicantDisabled,
      isMakeDecisionDisabled,
      isCancelApplicationDisabled,
      isRefundApplicationDisabled,
      isMoveToAnotherUnitDisabled,
      isUncancelApplicationEnabled
    };
  }, [
    applicationStatus,
    type,
    permissions,
    applicants,
    requiresOverridesIdentityVerification
  ]);

  const canMakeDecision = canDecisionBeMade({
    applicants: applicants || [],
    status: applicationStatus,
    type,
    permissions,
    requirements
  });

  return [
    {
      title: (
        <MenuRow
          text={`Move to another ${unit}`}
          Icon={ArrowRight}
          isDisabled={isMoveToAnotherUnitDisabled}
        />
      ),
      tooltip: `You can't move application to another ${unit} once the decision have been made`,
      disabled: isMoveToAnotherUnitDisabled,
      onClick: () => {
        onMoveClick && onMoveClick();
      }
    },
    isUncancelApplicationEnabled
      ? {
          title: <MenuRow text="Uncancel application" Icon={Archive} />,
          onClick: () => {
            onUncancelClick && onUncancelClick();
          }
        }
      : {
          title: (
            <MenuRow
              text="Cancel application"
              Icon={Archive}
              isDisabled={isCancelApplicationDisabled}
            />
          ),
          tooltip:
            applicationStatus === ApplicationStatus.Canceled
              ? "Can not cancel a canceled application"
              : "You can't cancel application once the decisions have been made",
          disabled: isCancelApplicationDisabled,
          onClick: () => {
            onCancelClick && onCancelClick();
          }
        },
    {
      title: (
        <MenuRow
          text="Refund application"
          Icon={FlipBackward}
          isDisabled={isRefundApplicationDisabled}
        />
      ),
      disabled: isRefundApplicationDisabled,
      tooltip: (applicants || []).some(
        (applicant: Applicant) =>
          applicant.application_status === ApplicantStatus.Submitted
      )
        ? (applicants || []).every(
            (applicant: Applicant) =>
              applicant.application_fee_status === ApplicationFeeStatus.Refunded
          )
          ? "Refund has already been requested for this application"
          : (applicants || []).some(
                (applicant: Applicant) =>
                  applicant.application_fee_status === ApplicationFeeStatus.Paid
              )
            ? ""
            : "You can request a refund if someone has paid an application fee"
        : "You can request a refund for submitted applications",
      onClick: () => onRefundClick && onRefundClick()
    },
    {
      title: <MenuRow text="Screening settings" Icon={Settings04} />,
      onClick: () => {
        viewTemplate();
      }
    },
    {
      title: <MenuRow text="Activity log" Icon={Clock} />,
      onClick: () => {
        setShowActivityLog(true);
      }
    },
    {
      title: (
        <MenuRow
          text="Invite applicant"
          Icon={Plus}
          isDisabled={isInviteApplicantDisabled}
        />
      ),
      tooltip: [
        ApplicationStatus.Approved,
        ApplicationStatus.Rejected,
        ApplicationStatus.ConditionalApproved,
        ApplicationStatus.Canceled,
        ApplicationStatus.Merged
      ].includes(applicationStatus)
        ? "You can't invite applicants once the decisions have been made"
        : undefined,
      disabled: isInviteApplicantDisabled,
      onClick: () => setShowInviteApplicantsModal?.(true)
    },
    {
      title: (
        <MenuRow
          text="Make decision"
          Icon={ThumbsUp}
          isDisabled={isMakeDecisionDisabled}
        />
      ),
      disabled: !canMakeDecision,
      tooltip: !canMakeDecision
        ? "You can make a decision when at least one application has been submitted"
        : undefined,
      onClick: () => {
        applicationStatus === ApplicationStatus.Submitted
          ? setShowDecisionFlowModal?.(true)
          : setDecisionConfirmationModal?.(true);
      }
    }
  ];
};
